if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sizeSelected = false;
        this.hasSizeOption = false;
        this.isInternalUpdate = false;
        this.rerenderDebounce = null;
        this.visibilityObserver = null;
        this._visibilityTarget = null;
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;
        this.button = this.querySelector('[data-sticky-atc-button]');
        this.textEl = this.querySelector('[data-sticky-atc-text]');

        this.moveToBody();
        document.addEventListener('shopify:section:load', () => this.moveToBody());

        this.init();
      }

      moveToBody() {
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
        }
      }

      isMobile() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      getRoot() {
        return document.querySelector(`#MainProduct-${this.sectionId}`) || document;
      }

      getProductInfoRoot() {
        return document.querySelector(`#ProductInfo-${this.sectionId}`) || this.getRoot();
      }

      findSizeFieldset() {
        const root = this.getRoot();

        const tagged = root.querySelector('[data-option-name="Size"]');
        if (tagged) {
          return tagged.closest('fieldset, .product-form__input') || root;
        }

        const fieldsets = Array.from(root.querySelectorAll('fieldset'));
        return (
          fieldsets.find((fs) => {
            const legend = fs.querySelector('legend');
            return legend && /^size/i.test(legend.textContent.trim());
          }) || null
        );
      }

      getSizeInputs() {
        const fieldset = this.findSizeFieldset();
        if (!fieldset) return [];

        this.sizeFieldset = fieldset;

        return Array.from(
          fieldset.querySelectorAll(
            'input[type="radio"], select.select__select, select.select__select option'
          )
        );
      }

      init() {
        this.setupVisibilityObserver();

        const sizeInputs = this.getSizeInputs();
        this.hasSizeOption = sizeInputs.length > 0;

        this.realSubmitButton = document.querySelector(`#${this.productFormId} [type="submit"]`);
        this.realSubmitButtonText = this.realSubmitButton
          ? this.realSubmitButton.querySelector('span')
          : null;
        this.defaultSubmitText = this.realSubmitButtonText
          ? this.realSubmitButtonText.textContent
          : 'Add to Cart';

        this.observeProductInfoForVisibility();

        if (!this.isMobile() || !this.hasSizeOption) {
          this.setState('ready');
          this.button.addEventListener('click', () => this.submitForm());
          return;
        }

        this.sizeInputs = sizeInputs;
        this.sizeSelected = false;

        // Sirf PAGE LOAD par hi checked state physically clear karte hain -
        // ye behavior shuru se hi tha aur ismein gallery ke saath kabhi
        // conflict nahi hua. Runtime resync (color change etc) me ab hum
        // checked state ko bilkul touch nahi karenge - sirf visual class
        // se "unselected" dikhayenge (neeche syncGateState me).
        sizeInputs.forEach((el) => {
          if (el.tagName === 'INPUT') el.checked = false;
          if (el.tagName === 'OPTION') el.selected = false;
        });

        const select = this.sizeFieldset.querySelector('select.select__select');
        if (select) select.selectedIndex = -1;

        this.applyUnconfirmedVisual();

        this.insertInlineError();

        this.gateAddToCartButton(true);
        this.setupPaymentButtonGate(true);

        this.setState('select-size');

        this.getRoot().addEventListener('change', (event) => {
          if (!event.target || !this.sizeFieldset) return;
          if (!this.sizeFieldset.contains(event.target)) return;
          if (!event.target.matches('input[type="radio"], select.select__select')) return;

          this.sizeSelected = true;
          this.hideSizeError();
          this.removeUnconfirmedVisual();
          this.gateAddToCartButton(false);
          this.setupPaymentButtonGate(false);
          this.setState('ready');
        });

        this.button.addEventListener('click', () => this.handleClick());

        this.observeProductInfoForGating();
      }

      // ---- Sticky bar visibility ---------------------------------------
      setupVisibilityObserver() {
        const target = document.querySelector(`#${this.productFormId}`);
        if (!target) return;
        if (this._visibilityTarget === target) return;

        if (this.visibilityObserver) this.visibilityObserver.disconnect();

        this._visibilityTarget = target;
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.classList.toggle('is-hidden', entry.isIntersecting);
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(target);
      }

      observeProductInfoForVisibility() {
        const root = this.getProductInfoRoot();
        if (!root) return;

        let debounce = null;
        const observer = new MutationObserver(() => {
          clearTimeout(debounce);
          debounce = setTimeout(() => this.setupVisibilityObserver(), 100);
        });
        observer.observe(root, { childList: true, subtree: true });
      }

      // ---- Re-render handling for size-gating --------------------------
      // IMPORTANT: ab ye kabhi bhi size radio ka native `checked` state
      // nahi chhedta - gallery script isi checked state par depend karke
      // variant match karta hai aur images filter karta hai. Yahan sirf
      // visual "unconfirmed" class + label text blank karte hain, jo
      // checked attribute se bilkul independent hai.
      observeProductInfoForGating() {
        const root = this.getProductInfoRoot();
        if (!root) return;

        const observer = new MutationObserver(() => {
          if (this.isInternalUpdate) return;

          clearTimeout(this.rerenderDebounce);
          this.rerenderDebounce = setTimeout(() => this.syncGateState(), 60);
        });

        observer.observe(root, { childList: true, subtree: true });
      }

      syncGateState() {
        if (!this.hasSizeOption || !this.isMobile()) return;

        this.isInternalUpdate = true;

        this.setupVisibilityObserver();

        this.sizeFieldset = this.findSizeFieldset();
        this.sizeInputs = this.getSizeInputs();

        this.realSubmitButton = document.querySelector(`#${this.productFormId} [type="submit"]`);
        this.realSubmitButtonText = this.realSubmitButton
          ? this.realSubmitButton.querySelector('span')
          : null;

        if (this.sizeErrorEl && !document.body.contains(this.sizeErrorEl)) {
          this.sizeErrorEl = null;
        }
        this.insertInlineError();

        if (!this.sizeSelected) {
          // Checked state ko touch NAHI karna - sirf visual "unselected"
          // look + label blank karo. Gallery ko full variant match milta
          // rahega, isliye images turant update hoti rahengi.
          this.applyUnconfirmedVisual();
          this.gateAddToCartButton(true);
          this.setupPaymentButtonGate(true);
          this.setState('select-size');
        } else {
          this.removeUnconfirmedVisual();
          this.gateAddToCartButton(false);
          this.setupPaymentButtonGate(false);
          this.setState('ready');
        }

        requestAnimationFrame(() => {
          setTimeout(() => {
            this.isInternalUpdate = false;
          }, 40);
        });
      }

      // Dark-highlighted pill ko wapas normal dikhana + "Size: XS" label
      // blank karna - purely visual, koi checked/value change nahi.
      applyUnconfirmedVisual() {
        if (this.sizeFieldset) {
          this.sizeFieldset.classList.add('size-not-confirmed');
        }
        const label = this.sizeFieldset && this.sizeFieldset.querySelector('[data-selected-value]');
        if (label) label.textContent = '';
      }

      removeUnconfirmedVisual() {
        if (this.sizeFieldset) {
          this.sizeFieldset.classList.remove('size-not-confirmed');
        }
      }

      handleClick() {
        if (this.hasSizeOption && !this.sizeSelected) {
          this.scrollToSizePicker();
          return;
        }
        this.submitForm();
      }

      scrollToSizePicker() {
        if (!this.sizeFieldset) return;
        this.sizeFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.sizeFieldset.classList.add('size-picker--pulse');
        setTimeout(() => this.sizeFieldset.classList.remove('size-picker--pulse'), 1200);
        this.showSizeError();
      }

      gateAddToCartButton(disable) {
        this.realSubmitButton = document.querySelector(`#${this.productFormId} [type="submit"]`);
        if (!this.realSubmitButton) return;

        const strayFakes = document.querySelectorAll(
          `#${this.productFormId} [data-fake-add-to-cart]`
        );
        if (strayFakes.length > 1) {
          Array.from(strayFakes).slice(0, -1).forEach((el) => el.remove());
        }
        this.fakeAddToCartButton = strayFakes.length ? strayFakes[strayFakes.length - 1] : null;

        if (disable) {
          if (!this.fakeAddToCartButton) {
            const fake = document.createElement('button');
            fake.type = 'button';
            fake.className = this.realSubmitButton.className;
            fake.setAttribute('data-fake-add-to-cart', '');
            fake.innerHTML = this.realSubmitButton.innerHTML;

            const fakeSpan = fake.querySelector('span');
            if (fakeSpan) fakeSpan.textContent = 'Add To Cart';

            fake.addEventListener('click', (event) => {
              event.preventDefault();
              this.scrollToSizePicker();
            });

            this.realSubmitButton.insertAdjacentElement('beforebegin', fake);
            this.fakeAddToCartButton = fake;
          }
          this.realSubmitButton.style.display = 'none';
          this.fakeAddToCartButton.style.display = '';
        } else {
          this.realSubmitButton.style.display = '';
          if (this.fakeAddToCartButton) {
            this.fakeAddToCartButton.style.display = 'none';
          }
        }
      }

      insertInlineError() {
        if (this.sizeErrorEl && document.body.contains(this.sizeErrorEl)) return;

        const legend = this.sizeFieldset && this.sizeFieldset.querySelector('legend');
        const labelHost =
          (legend && legend.querySelector('span')) || legend || this.sizeFieldset;

        if (!labelHost) return;

        const error = document.createElement('span');
        error.setAttribute('data-size-select-error', '');
        error.setAttribute('role', 'alert');
        error.className = 'size-select-error';
        error.textContent = 'Please select a size';
        labelHost.insertAdjacentElement('afterend', error);
        this.sizeErrorEl = error;
      }

      showSizeError() {
        if (this.sizeErrorEl) this.sizeErrorEl.classList.add('is-visible');
      }

      hideSizeError() {
        if (this.sizeErrorEl) this.sizeErrorEl.classList.remove('is-visible');
      }

      setupPaymentButtonGate(disable) {
        const wraps = document.querySelectorAll(`#${this.productFormId} .shopify-payment-button`);
        if (wraps.length > 1) {
          Array.from(wraps).slice(0, -1).forEach((el) => el.remove());
        }

        this.paymentButtonWrap = document.querySelector(
          `#${this.productFormId} .shopify-payment-button`
        );
        if (!this.paymentButtonWrap) return;

        this.paymentButtonWrap.classList.add('size-gate-host');

        if (disable) {
          this.paymentButtonWrap.classList.add('size-gate--disabled');
          let overlay = this.paymentButtonWrap.querySelector('[data-size-gate-overlay]');
          if (!overlay) {
            overlay = document.createElement('div');
            overlay.className = 'size-gate-overlay';
            overlay.setAttribute('data-size-gate-overlay', '');
            overlay.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              this.scrollToSizePicker();
            });
            this.paymentButtonWrap.appendChild(overlay);
          }
          this.paymentOverlay = overlay;
        } else {
          this.paymentButtonWrap.classList.remove('size-gate--disabled');
          const overlay = this.paymentButtonWrap.querySelector('[data-size-gate-overlay]');
          if (overlay) overlay.remove();
          this.paymentOverlay = null;
        }
      }

      submitForm() {
        if (!this.realSubmitButton || this.realSubmitButton.style.display === 'none') {
          return;
        }
        this.setState('adding');
        this.realSubmitButton.click();
        setTimeout(() => this.setState('ready'), 1200);
      }

      setState(state) {
        if (!this.button) return;
        this.button.dataset.state = state;
        if (state === 'select-size') this.textEl.textContent = 'Please select a size';
        else if (state === 'adding') this.textEl.textContent = 'Adding...';
        else this.textEl.textContent = 'Add to Cart';
      }
    }
  );
}